import { ReactNode } from "react";
import { renderToString } from "react-dom/server";
import { FieldPath, HttpsUrl } from "./amp-list-utils";
import { createMustacheContext, MustacheContext } from "./amp-variable";

export type AmpLayout =
  | "fixed-height"
  | "flex-item"
  | "responsive"
  | "container"
  | "fill"
  | "fixed"
  | "nodisplay"
  | "fixed-height";

export type AmpListLayout = Extract<
  AmpLayout,
  "fixed-height" | "fixed" | "fill" | "flex-item" | "nodisplay" | "responsive"
>;

export type AmpListProps<T, Path extends FieldPath<T>> = {
  id: string;
  src: HttpsUrl;
  items: Path;
  maxItems?: number;
  height: number;
  layout: AmpListLayout;
  loadingPlaceholder?: ReactNode;
  overflowButton?: ReactNode;
  fallback?: ReactNode;
  template: (context: MustacheContext<T, Path>) => ReactNode;
  singleItem: boolean;
  columns?: 2 | 3 | 4;
};

/**
 * The generics are a bit intimidating, but the usage is simple.
 * Their purpose is to ensure that the `template` function can only
 * receive variables that actually exist in the object at the path specified
 * by `items`. The only danger is that you might accidentally provide an *array* where
 * an *object* was needed. Make sure the type of field you choose corresponds with `singleItem`
 */
export function AmpList<T, Path extends FieldPath<T>>(
  props: AmpListProps<T, Path>,
) {
  const scope = createMustacheContext<T, Path>();
  return (
    <amp-list
      class={(props.columns ?? 1) > 1 ? `col-${props.columns}` : ""}
      dangerouslySetInnerHTML={{
        __html: renderToString(
          <>
            <div>{props.loadingPlaceholder}</div>
            <div>{props.fallback}</div>
            <template type="amp-mustache">{props.template(scope)}</template>
            <div
              aria-label="Show more"
              className="list-overflow"
              overflow=""
              role="button"
              style={{ width: "100%", bottom: 0 }}
            >
              {props.overflowButton}
            </div>
          </>,
        ),
      }}
      height={props.height}
      id={props.id}
      items={props.items}
      layout={props.layout}
      max-items={props.maxItems}
      single-item={props.singleItem ? "" : undefined}
      src={props.src}
    />
  );
}
